import { useState } from 'react';
import Breadcrumb from '../../components/Breadcrumb';
import { useToast } from '../../components/Toast';
import { mergeProjectAssetCase, AssetMergeStats } from '../../api/adminProjects';

export default function AssetMerge() {
  const toast = useToast();
  const [projectId, setProjectId] = useState('');
  const [loading, setLoading] = useState(false);
  const [dryStats, setDryStats] = useState<AssetMergeStats | null>(null);
  const [doneStats, setDoneStats] = useState<AssetMergeStats | null>(null);

  const run = async (confirmed: boolean) => {
    setLoading(true);
    if (!confirmed) setDoneStats(null);
    try {
      const stats = await mergeProjectAssetCase(projectId.trim(), confirmed);
      if (confirmed) {
        setDoneStats(stats);
        toast.success(`合并完成，已合并 ${stats.mergedGroups} 组${stats.assetJsonRebuilt ? '' : '（资产JSON待重试）'}`);
      } else {
        setDryStats(stats);
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : '请求失败');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container" style={{ maxWidth: 1100, margin: '0 auto', padding: '2rem 1rem' }}>
      <Breadcrumb items={[{ label: '首页', to: '/projects' }, { label: '合并资产' }]} />
      <div className="page-header"><h1 className="page-title">合并资产</h1></div>

      <div className="card" style={{ padding: '1rem', marginBottom: '1rem' }}>
        <p style={{ color: 'var(--text-2)', marginTop: 0 }}>
          按项目把人物资产里同名不同大小写的角色（如 <code>CLARA HART</code> 与 <code>Clara Hart</code>）合并为规范名，并同步重写分镜等全部引用。先预检（不写库），确认后再合并；可重复执行。
        </p>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            className="input"
            placeholder="项目 ID"
            aria-label="项目 ID"
            value={projectId}
            onChange={(e) => { setProjectId(e.target.value); setDryStats(null); setDoneStats(null); }}
            style={{ flex: 1, minWidth: 260 }}
          />
          <button className="btn btn-ghost" onClick={() => run(false)} disabled={loading || !projectId.trim()}>预检</button>
          <button className="btn btn-primary" onClick={() => run(true)} disabled={loading || !dryStats || dryStats.advancedAnalysisInProgress}>确认合并</button>
        </div>
      </div>

      {dryStats && !doneStats && (
        <div className="card" style={{ padding: '1rem', marginBottom: '1rem' }}>
          <h2 className="page-title" style={{ fontSize: '1.05rem', marginTop: 0 }}>预检结果</h2>
          <div>待合并 <b>{dryStats.totalGroups}</b> 组 / {dryStats.totalVariants} 个变体；被跳过 {dryStats.skippedGroups}</div>
          {dryStats.advancedAnalysisInProgress && (
            <div style={{ marginTop: '0.5rem', color: 'var(--danger, #d33)' }}>
              ⚠ 高级分析进行中：确认合并会被拒绝（409）。请先完成或取消高级分析。
            </div>
          )}
          {(dryStats.groups ?? []).length === 0 && (
            <div style={{ marginTop: '0.4rem', color: 'var(--text-2)' }}>没有需要合并的同名大小写资产。</div>
          )}
          {(dryStats.groups ?? []).map((g) => (
            <div key={g.canonicalName} style={{ fontFamily: 'monospace', fontSize: '0.85rem', marginTop: '0.4rem' }}>
              {g.canonicalName} ← {(g.variants ?? []).map((v) => `${v.name}(${v.entryCount})`).join('、')}
              <span style={{ color: 'var(--text-2)' }}>
                　分镜 {g.shotAssetRefs}/版本 {g.versionRefs}/审核 {g.reviewRows}/风格测试 {g.styleTests}/造型结果 {g.styleResults}/图任务 {g.imageTasks}/搜索改名 {g.searchRenames}+删 {g.searchDeletes}
                {(g.castingDeletes + g.stylingDeletes) > 0 ? `，删造型选角 ${g.castingDeletes + g.stylingDeletes}` : ''}
              </span>
            </div>
          ))}
          {(dryStats.blocked ?? []).length > 0 && (
            <div style={{ marginTop: '0.6rem', color: 'var(--danger, #d33)' }}>
              <div style={{ fontWeight: 600 }}>阻断（活动/待持久化图任务，本次跳过）：</div>
              {(dryStats.blocked ?? []).map((b) => <div key={b.canonicalName}>{b.canonicalName}：{b.reason}</div>)}
            </div>
          )}
        </div>
      )}

      {doneStats && (
        <div className="card" style={{ padding: '1rem', marginBottom: '1rem' }}>
          <h2 className="page-title" style={{ fontSize: '1.05rem', marginTop: 0 }}>合并完成</h2>
          <div>已合并 <b>{doneStats.mergedGroups}</b> 组，跳过 {doneStats.skippedGroups}；
            资产JSON {doneStats.assetJsonRebuilt ? '已重建' : '待重试（再次确认即可重建）'}</div>
        </div>
      )}
    </div>
  );
}
